import React, { useState, useEffect, useCallback } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  Collapse,
  ListSubheader,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Router,
  People,
  Receipt,
  RequestQuote,
  LocalOffer,
  AccountCircle,
  Logout,
  LockReset,
  Settings,
  Storage,
  ExpandLess,
  ExpandMore,
  AccountTree,
  Hub,
  CellTower,
  WhatsApp,
  Payment,
  ManageAccounts,
  Assessment,
  Campaign,
  History,
  ListAlt,
  Article,
  Timer,
  Palette,
  DeviceHub,
  SignalCellularAlt,
  SettingsEthernet,
  SmartToy,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import AppBrand from './AppBrand';
import ChangePasswordDialog from './ChangePasswordDialog';

const drawerWidth = 260;

interface MenuItemDef {
  text: string;
  icon: React.ReactNode;
  path: string;
  roles?: Array<'admin' | 'operator' | 'viewer'>;
}

interface LayoutProps {
  children: React.ReactNode;
}

const selectedSx = {
  '&.Mui-selected': {
    backgroundColor: 'primary.main',
    color: 'primary.contrastText',
    '&:hover': { backgroundColor: 'primary.dark' },
    '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
  },
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [networkOpen, setNetworkOpen] = useState(false);
  const [masterDataOpen, setMasterDataOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [whatsappOpen, setWhatsappOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === 'admin';
  const isOperator = user?.role === 'operator';

  const canSee = useCallback(
    (item: MenuItemDef) => {
      if (!item.roles || !user?.role) return true;
      return item.roles.includes(user.role as 'admin' | 'operator' | 'viewer');
    },
    [user?.role]
  );

  const mainItems: MenuItemDef[] = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/' },
    { text: 'Pelanggan', icon: <People />, path: '/customers' },
    { text: 'Tagihan', icon: <RequestQuote />, path: '/bills' },
    { text: 'Transaksi', icon: <Receipt />, path: '/transactions' },
  ];

  const networkItems: MenuItemDef[] = [
    { text: 'Monitoring OLT', icon: <SignalCellularAlt />, path: '/olts/dashboard' },
    { text: 'Kelola OLT', icon: <DeviceHub />, path: '/olts' },
    { text: 'Router', icon: <Router />, path: '/routers' },
  ];

  const masterDataItems: MenuItemDef[] = [
    { text: 'SPOP', icon: <CellTower />, path: '/master-data/spop' },
    { text: 'ODC', icon: <AccountTree />, path: '/master-data/odc' },
    { text: 'ODP', icon: <Hub />, path: '/master-data/odp' },
    { text: 'Paket Internet', icon: <LocalOffer />, path: '/packages' },
  ];

  const reportsItems: MenuItemDef[] = [
    { text: 'Pendapatan SPOP', icon: <Assessment />, path: '/reports/spop-revenue' },
  ];

  const whatsappItems: MenuItemDef[] = [
    { text: 'Broadcast', icon: <Campaign />, path: '/whatsapp/broadcast' },
    { text: 'Riwayat Broadcast', icon: <History />, path: '/whatsapp/broadcast/history' },
    { text: 'Log Antrian', icon: <ListAlt />, path: '/whatsapp/queue/log' },
    ...(isOperator
      ? [{ text: 'Template Pesan', icon: <Article />, path: '/settings/whatsapp' }]
      : []),
  ];

  const settingsItems: MenuItemDef[] = [
    { text: 'Tripay', icon: <Payment />, path: '/settings/tripay', roles: ['admin'] },
    { text: 'PayBOS', icon: <SettingsEthernet />, path: '/settings/paybos', roles: ['admin'] },
    { text: 'WhatsApp', icon: <WhatsApp />, path: '/settings/whatsapp', roles: ['admin'] },
    { text: 'Tampilan Website', icon: <Palette />, path: '/settings/branding', roles: ['admin'] },
    { text: 'Scheduler', icon: <Timer />, path: '/settings/scheduler', roles: ['admin'] },
    { text: 'CS AI API', icon: <SmartToy />, path: '/settings/cs-api', roles: ['admin'] },
  ];

  const isPathActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    if (path === '/olts') {
      return (
        location.pathname === '/olts' ||
        (location.pathname.startsWith('/olts/') && !location.pathname.startsWith('/olts/dashboard'))
      );
    }
    if (path === '/olts/dashboard') {
      return location.pathname === '/olts/dashboard';
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const isSelectedNetwork =
    location.pathname.startsWith('/olts') || location.pathname.startsWith('/routers');

  const isSelectedMasterData =
    location.pathname.startsWith('/master-data') || location.pathname.startsWith('/packages');

  const isSelectedReports = location.pathname.startsWith('/reports');

  const isSelectedWhatsapp =
    location.pathname.startsWith('/whatsapp') ||
    (isOperator && location.pathname.startsWith('/settings/whatsapp'));

  const isSelectedSettings =
    location.pathname.startsWith('/settings') && !(isOperator && location.pathname === '/settings/whatsapp');

  // Auto-expand submenu sesuai halaman aktif
  useEffect(() => {
    if (isSelectedNetwork) setNetworkOpen(true);
    if (isSelectedMasterData) setMasterDataOpen(true);
    if (isSelectedReports) setReportsOpen(true);
    if (isSelectedWhatsapp) setWhatsappOpen(true);
    if (isSelectedSettings) setSettingsOpen(true);
  }, [location.pathname]);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleProfileMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleProfileMenuClose();
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const renderMenuItem = (item: MenuItemDef, nested = false) => {
    if (!canSee(item)) return null;
    return (
      <ListItem key={item.path} disablePadding>
        <ListItemButton
          selected={isPathActive(item.path)}
          onClick={() => handleNavigation(item.path)}
          sx={{ ...selectedSx, ...(nested ? { pl: 4 } : {}) }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
          <ListItemText
            primary={item.text}
            primaryTypographyProps={{ fontSize: nested ? '0.875rem' : '0.9rem' }}
          />
        </ListItemButton>
      </ListItem>
    );
  };

  const renderSubmenu = (
    label: string,
    icon: React.ReactNode,
    open: boolean,
    onToggle: () => void,
    selected: boolean,
    items: MenuItemDef[]
  ) => {
    const visible = items.filter(canSee);
    if (visible.length === 0) return null;
    return (
      <>
        <ListItem disablePadding>
          <ListItemButton selected={selected} onClick={onToggle} sx={selectedSx}>
            <ListItemIcon sx={{ minWidth: 40 }}>{icon}</ListItemIcon>
            <ListItemText primary={label} primaryTypographyProps={{ fontSize: '0.9rem' }} />
            {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </ListItemButton>
        </ListItem>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {visible.map((item) => renderMenuItem(item, true))}
          </List>
        </Collapse>
      </>
    );
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar sx={{ px: 1.5, minHeight: { xs: 56, sm: 64 } }}>
        <AppBrand variant="sidebar" />
      </Toolbar>
      <Divider />

      {isOperator && user?.spop && (
        <Box sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}>
          <Typography variant="caption" color="text.secondary" display="block">
            SPOP Anda
          </Typography>
          <Typography variant="body2" fontWeight={600} noWrap>
            {user.spop.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user.spop.areaCode}
          </Typography>
        </Box>
      )}

      <List sx={{ flex: 1, py: 1 }}>
        <ListSubheader sx={{ bgcolor: 'transparent', lineHeight: '28px', fontSize: '0.7rem', fontWeight: 700 }}>
          UTAMA
        </ListSubheader>
        {mainItems.map((item) => renderMenuItem(item))}

        <Divider sx={{ my: 1 }} />
        <ListSubheader sx={{ bgcolor: 'transparent', lineHeight: '28px', fontSize: '0.7rem', fontWeight: 700 }}>
          JARINGAN
        </ListSubheader>
        {renderSubmenu('Infrastruktur', <DeviceHub />, networkOpen, () => setNetworkOpen(!networkOpen), isSelectedNetwork, networkItems)}

        <Divider sx={{ my: 1 }} />
        <ListSubheader sx={{ bgcolor: 'transparent', lineHeight: '28px', fontSize: '0.7rem', fontWeight: 700 }}>
          DATA
        </ListSubheader>
        {renderSubmenu('Master Data', <Storage />, masterDataOpen, () => setMasterDataOpen(!masterDataOpen), isSelectedMasterData, masterDataItems)}

        <Divider sx={{ my: 1 }} />
        <ListSubheader sx={{ bgcolor: 'transparent', lineHeight: '28px', fontSize: '0.7rem', fontWeight: 700 }}>
          LAPORAN
        </ListSubheader>
        {renderSubmenu('Laporan', <Assessment />, reportsOpen, () => setReportsOpen(!reportsOpen), isSelectedReports, reportsItems)}

        <Divider sx={{ my: 1 }} />
        <ListSubheader sx={{ bgcolor: 'transparent', lineHeight: '28px', fontSize: '0.7rem', fontWeight: 700 }}>
          KOMUNIKASI
        </ListSubheader>
        {renderSubmenu('WhatsApp', <WhatsApp />, whatsappOpen, () => setWhatsappOpen(!whatsappOpen), isSelectedWhatsapp, whatsappItems)}

        {isAdmin && (
          <>
            <Divider sx={{ my: 1 }} />
            <ListSubheader sx={{ bgcolor: 'transparent', lineHeight: '28px', fontSize: '0.7rem', fontWeight: 700 }}>
              SISTEM
            </ListSubheader>
            {renderSubmenu('Pengaturan', <Settings />, settingsOpen, () => setSettingsOpen(!settingsOpen), isSelectedSettings, settingsItems)}
            {renderMenuItem({ text: 'Manajemen User', icon: <ManageAccounts />, path: '/users' })}
          </>
        )}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }} />

          {isAdmin && (
            <Tooltip title="Pengaturan">
              <IconButton color="inherit" onClick={() => navigate('/settings/tripay')}>
                <Settings />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Profil">
            <IconButton onClick={handleProfileMenuOpen} color="inherit">
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                {user?.name?.charAt(0).toUpperCase() || user?.username?.charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem disabled sx={{ opacity: 1 }}>
          <ListItemIcon>
            <AccountCircle fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={user?.name || user?.username}
            secondary={`${user?.role}${user?.spop ? ` • ${user.spop.name}` : ''}`}
          />
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            handleProfileMenuClose();
            setChangePasswordOpen(true);
          }}
        >
          <ListItemIcon>
            <LockReset fontSize="small" />
          </ListItemIcon>
          Ubah Password
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Keluar
        </MenuItem>
      </Menu>

      <ChangePasswordDialog
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
        user={user}
      />

      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
